import logoUrl from "../qr-icon.png";
import { BRAND_BLUE, BRAND_VIOLET, GRADIENT_ANGLE_DEG, SOLID_COLORS, type ColorStyle } from "./style";

/**
 * Convert a black-on-white RGBA bitmap into a mask: alpha follows darkness (black → opaque,
 * white → transparent, anti-aliased edges → partial), colour channels are zeroed. Pure, so it is
 * unit-testable without a DOM. Mutates `px` in place and returns it.
 */
export function luminanceToAlpha(px: Uint8ClampedArray): Uint8ClampedArray {
	for (let i = 0; i < px.length; i += 4) {
		const lum = 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
		const alpha = Math.round(((255 - lum) * px[i + 3]) / 255);
		px[i] = 0;
		px[i + 1] = 0;
		px[i + 2] = 0;
		px[i + 3] = alpha;
	}
	return px;
}

/** Start/end points of a CSS-angle linear gradient across a w×h box (0° = up, 90° = right). */
export function gradientLine(w: number, h: number, angleDeg: number): { x0: number; y0: number; x1: number; y1: number } {
	const rad = (angleDeg * Math.PI) / 180;
	const dx = Math.sin(rad);
	const dy = -Math.cos(rad);
	const len = Math.abs(w * dx) + Math.abs(h * dy);
	const cx = w / 2;
	const cy = h / 2;
	return { x0: cx - (dx * len) / 2, y0: cy - (dy * len) / 2, x1: cx + (dx * len) / 2, y1: cy + (dy * len) / 2 };
}

const cache = new Map<ColorStyle, Promise<string>>();

/** URL of the logo tinted to match the chosen QR colour. Black uses the original PNG untouched. */
export function logoForColor(color: ColorStyle): Promise<string> {
	if (color === "black") return Promise.resolve(logoUrl);
	let pending = cache.get(color);
	if (!pending) {
		pending = recolor(color).catch((err) => {
			cache.delete(color);
			throw err;
		});
		cache.set(color, pending);
	}
	return pending;
}

async function recolor(color: ColorStyle): Promise<string> {
	const img = await loadImage(logoUrl);
	const canvas = document.createElement("canvas");
	canvas.width = img.naturalWidth;
	canvas.height = img.naturalHeight;
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Canvas 2D is unavailable");

	ctx.drawImage(img, 0, 0);
	const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
	luminanceToAlpha(data.data);
	ctx.putImageData(data, 0, 0);

	// Paint the colour only where the mask is opaque.
	ctx.globalCompositeOperation = "source-in";
	if (color === "gradient") {
		const { x0, y0, x1, y1 } = gradientLine(canvas.width, canvas.height, GRADIENT_ANGLE_DEG);
		const g = ctx.createLinearGradient(x0, y0, x1, y1);
		g.addColorStop(0, BRAND_BLUE);
		g.addColorStop(1, BRAND_VIOLET);
		ctx.fillStyle = g;
	} else {
		ctx.fillStyle = SOLID_COLORS[color];
	}
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	// Put the white background back so the logo area stays a clean quiet zone in the code.
	ctx.globalCompositeOperation = "destination-over";
	ctx.fillStyle = "#ffffff";
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	return canvas.toDataURL("image/png");
}

function loadImage(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error("Could not load the logo image"));
		img.src = src;
	});
}
